import React, { useState, useEffect } from 'react';
import { Icon, StatusPulse, CodeToken, CopyButton, ServerLogo, Select, SelectItem } from '../components/ui';
import { useTranslation } from 'react-i18next';

interface OnboardingPortalProps {
  apiUrl: string;
  portalMode?: boolean;
}

interface AvailableService {
  server_id: string;
  name: string;
  status: string;
  health_status: string;
  gateway_uri?: string;
  suggested_prompts?: string[];
  tools: Array<{ name: string; description: string }>;
}

const OnboardingPortal: React.FC<OnboardingPortalProps> = ({ apiUrl, portalMode = false }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [availableServices, setAvailableServices] = useState<AvailableService[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    agentName: '',
    role: 'developer',
    serviceIds: [] as string[],
    description: ''
  });

  // Success state
  const [requestId, setRequestId] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);

  // Load available services on mount
  useEffect(() => {
    fetchAvailableServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const fetchAvailableServices = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/mcp/available-services`);
      if (response.ok) {
        const data = await response.json();
        setAvailableServices(data.services || []);
      } else {
        setError('Failed to load available MCP services');
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
      setError('Error loading MCP services. Make sure the firewall is running.');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleServiceToggle = (serviceId: string) => {
    setFormData((prev) => ({
      ...prev,
      serviceIds: prev.serviceIds.includes(serviceId)
        ? prev.serviceIds.filter((id) => id !== serviceId)
        : [...prev.serviceIds, serviceId]
    }));
  };

  const validateStep = (): boolean => {
    if (step === 1) {
      if (!formData.agentName.trim()) {
        setError('Agent name is required');
        return false;
      }
      if (formData.agentName.length < 3 || formData.agentName.length > 50) {
        setError('Agent name must be between 3 and 50 characters');
        return false;
      }
      if (!formData.role) {
        setError('Role is required');
        return false;
      }
      return true;
    }

    if (step === 2) {
      if (formData.serviceIds.length === 0) {
        setError('Select at least one MCP service');
        return false;
      }
      return true;
    }

    if (step === 3) {
      if (!formData.description.trim()) {
        setError('Use case description is required');
        return false;
      }
      if (formData.description.length > 500) {
        setError('Description must be 500 characters or less');
        return false;
      }
      return true;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiUrl}/onboarding/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_name: formData.agentName,
          role: formData.role,
          service_ids: formData.serviceIds,
          description: formData.description
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRequestId(data.request_id);
        setAgentId(data.agent_id);
        setStep(5); // Go to success screen
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to create onboarding request');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError('Error submitting request');
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = (role: string) => {
    if (role === 'viewer') return 'Viewer (read-only)';
    if (role === 'developer') return 'Developer (read & write)';
    if (role === 'admin') return 'Admin (full access)';
    return role;
  };

  // ============================================================
  // STEP 1: Agent Info
  // ============================================================
  const renderStep1 = () => (
    <div className="ent-card">
      <div className="ent-card-head">
        <h3>
          <Icon name="clipboardList" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {t('pages.onboardingPortal.step1AgentInformation')}
        </h3>
      </div>
      <div className="ent-card-body">
        <p className="muted-text" style={{ marginBottom: 20 }}>{t('pages.onboardingPortal.giveYourAgentANameAnd')}</p>

        <div className="ent-field">
          <label>{t('pages.onboardingPortal.agentName')}</label>
          <input
            className="ent-input"
            type="text"
            value={formData.agentName}
            onChange={(e) => handleInputChange('agentName', e.target.value)}
            placeholder={t('pages.onboardingPortal.eGMyDataAnalyzer')}
            maxLength={50}
          />
          <small style={{ color: 'var(--text-3)', fontSize: 12 }}>{t('pages.onboardingPortal.50Characters', { v0: formData.agentName.length })}</small>
        </div>

        <div className="ent-field" style={{ marginBottom: 0 }}>
          <label>{t('pages.onboardingPortal.role')}</label>
          <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v)}>
            <SelectItem value="viewer">{t('pages.onboardingPortal.viewerReadOnlyAccessToTools')}</SelectItem>
            <SelectItem value="developer">{t('pages.onboardingPortal.developerReadWriteAccess')}</SelectItem>
            <SelectItem value="admin">{t('pages.onboardingPortal.adminFullAccess')}</SelectItem>
          </Select>
          <small style={{ color: 'var(--text-3)', fontSize: 12 }}>
            {formData.role === 'viewer' && t('pages.onboardingPortal.roleViewer')}
            {formData.role === 'developer' && t('pages.onboardingPortal.roleDeveloper')}
            {formData.role === 'admin' && t('pages.onboardingPortal.roleAdmin')}
          </small>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // STEP 2: Select MCP Servers
  // ============================================================
  const renderStep2 = () => (
    <div className="ent-card">
      <div className="ent-card-head">
        <h3>
          <Icon name="server" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {t('pages.onboardingPortal.step2SelectMCPServices')}
        </h3>
        <span className="muted-text" style={{ fontSize: 12 }}>{t('pages.onboardingPortal.selected', { v0: formData.serviceIds.length })}</span>
      </div>
      <div className="ent-card-body">
        <p className="muted-text" style={{ marginBottom: 20 }}>
          {t('pages.onboardingPortal.chooseWhichMCPServicesYourAgent')}
        </p>

        {availableServices.length === 0 ? (
          <div className="ent-empty">
            <span className="icon">
              <Icon name="server" />
            </span>
            <h4>{t('pages.onboardingPortal.noMCPServicesAvailable')}</h4>
            <p>{t('pages.onboardingPortal.pleaseAskYourAdministratorToConfigure')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {availableServices.map((service) => {
              const selected = formData.serviceIds.includes(service.server_id);
              return (
                <div
                  key={service.server_id}
                  onClick={() => handleServiceToggle(service.server_id)}
                  style={{
                    padding: 16,
                    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                    background: selected ? 'color-mix(in srgb, var(--accent) 5%, transparent)' : 'var(--surface-2)',
                    transition: 'border-color 0.13s var(--ease), background 0.13s var(--ease)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ServerLogo hostname={service.name} apiUrl={apiUrl} proxyService={service.name} size={18} />
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{service.name}</h3>
                    </div>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleServiceToggle(service.server_id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: 16, height: 16, accentColor: 'var(--accent)' }}
                    />
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <StatusPulse
                      state={service.health_status === 'healthy' ? 'healthy' : service.health_status === 'unhealthy' ? 'unhealthy' : 'unknown'}
                    />
                  </div>

                  {service.suggested_prompts && service.suggested_prompts.length > 0 && (
                    <div style={{ marginBottom: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Icon name="bolt" size={12} /> {t('pages.onboardingPortal.tryAsking')}
                      </div>
                      {service.suggested_prompts.slice(0, 2).map((prompt, idx) => (
                        <div key={idx} style={{ fontSize: 11, marginBottom: 2, lineHeight: 1.3, color: 'var(--text-3)' }}>
                          • "{prompt}"
                        </div>
                      ))}
                      {service.suggested_prompts.length > 2 && (
                        <div style={{ fontSize: 11, marginTop: 2, color: 'var(--text-3)' }}>
                          {t('pages.onboardingPortal.moreExamples', { count: service.suggested_prompts.length - 2 })}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                    <strong>{service.tools && service.tools.length > 0 ? t('pages.onboardingPortal.toolsAvailable', { count: service.tools.length }) : '?'}</strong>
                    {service.tools && service.tools.length > 0 && (
                      <div style={{ marginTop: 8, lineHeight: 1.5 }}>
                        {service.tools.slice(0, 3).map((tool) => (
                          <div key={tool.name} style={{ fontSize: 11, color: 'var(--text-3)' }}>
                            • {tool.name}
                          </div>
                        ))}
                        {service.tools.length > 3 && (
                          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('pages.onboardingPortal.more', { v0: service.tools.length - 3 })}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {formData.serviceIds.length > 0 && (
          <div className="notice info" style={{ marginTop: 16, marginBottom: 0 }}>
            <Icon name="checkCircle" />
            <div>
              <strong>
                {t('pages.onboardingPortal.selectedServices', { count: formData.serviceIds.length })}
              </strong>
              <div style={{ marginTop: 2, fontSize: 12, color: 'var(--text-2)' }}>
                {availableServices
                  .filter((s) => formData.serviceIds.includes(s.server_id))
                  .map((s) => s.name)
                  .join(', ')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================================
  // STEP 3: Description
  // ============================================================
  const renderStep3 = () => (
    <div className="ent-card">
      <div className="ent-card-head">
        <h3>
          <Icon name="document" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {t('pages.onboardingPortal.step3UseCase')}
        </h3>
      </div>
      <div className="ent-card-body">
        <p className="muted-text" style={{ marginBottom: 20 }}>
          {t('pages.onboardingPortal.tellTheAdministratorWhyYouNeed')}
        </p>

        <div className="ent-field" style={{ marginBottom: 0 }}>
          <label>{t('pages.onboardingPortal.description')}</label>
          <textarea
            className="ent-textarea"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder={t('pages.onboardingPortal.eGBuildingAutomatedMLPipeline')}
            maxLength={500}
            rows={6}
            style={{ resize: 'vertical' }}
          />
          <small style={{ color: 'var(--text-3)', fontSize: 12 }}>{t('pages.onboardingPortal.500Characters', { v0: formData.description.length })}</small>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // STEP 4: Review
  // ============================================================
  const renderStep4 = () => (
    <div className="ent-card">
      <div className="ent-card-head">
        <h3>
          <Icon name="checkCircle" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {t('pages.onboardingPortal.step4ReviewYourRequest')}
        </h3>
      </div>
      <div className="ent-card-body">
        <p className="muted-text" style={{ marginBottom: 20 }}>
          {t('pages.onboardingPortal.pleaseReviewYourRequestBeforeSubmitting')}
        </p>

        <div className="detail-grid" style={{ gridTemplateColumns: '1fr', gap: 16 }}>
          <div>
            <span className="detail-label">{t('pages.onboardingPortal.agentName2')}</span>
            <span style={{ fontSize: 14, color: 'var(--text)' }}>{formData.agentName}</span>
          </div>
          <div>
            <span className="detail-label">{t('pages.onboardingPortal.role2')}</span>
            <span style={{ fontSize: 14, color: 'var(--text)' }}>{roleLabel(formData.role)}</span>
          </div>
          <div>
            <span className="detail-label">{t('pages.onboardingPortal.servicesRequested')}</span>
            <div className="token-row">
              {formData.serviceIds.map((id) => {
                const service = availableServices.find((s) => s.server_id === id);
                return service ? (
                  <CodeToken key={id}>{t('pages.onboardingPortal.tools', { v0: service.name, v1: service.tools.length })}</CodeToken>
                ) : null;
              })}
            </div>
          </div>
          {formData.description && (
            <div>
              <span className="detail-label">{t('pages.onboardingPortal.useCase')}</span>
              <span style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-2)' }}>{formData.description}</span>
            </div>
          )}
        </div>

        <div className="notice info" style={{ marginTop: 20, marginBottom: 0 }}>
          <Icon name="info" />
          <div>
            <strong>{t('pages.onboardingPortal.whatHappensNext')}</strong> {t('pages.onboardingPortal.anAdministratorWillReviewYourRequest')}
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // STEP 5: Success
  // ============================================================
  const renderStep5 = () => (
    <div className="ent-card">
      <div className="ent-card-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            width: 64,
            height: 64,
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: 'rgba(34, 197, 94, 0.12)',
            color: 'var(--success)'
          }}
        >
          <Icon name="checkCircle" size={36} />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
          {t('pages.onboardingPortal.requestSubmittedSuccessfully')}
        </h2>
        <p className="muted-text" style={{ marginBottom: 24 }}>
          {t('pages.onboardingPortal.yourOnboardingRequestHasBeenCreated')}
        </p>

        {requestId && agentId && (
          <div className="detail-grid" style={{ gridTemplateColumns: '1fr', textAlign: 'left', marginBottom: 24 }}>
            <div>
              <span className="detail-label">{t('pages.onboardingPortal.requestID')}</span>
              <span className="copy-cell">
                <CodeToken>{requestId}</CodeToken>
                <CopyButton value={requestId} title={t('pages.onboardingPortal.copyRequestID')} />
              </span>
            </div>
            <div>
              <span className="detail-label">{t('pages.onboardingPortal.agentID')}</span>
              <span className="copy-cell">
                <CodeToken>{agentId}</CodeToken>
                <CopyButton value={agentId} title={t('pages.onboardingPortal.copyAgentID')} />
              </span>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 24, textAlign: 'left' }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>{t('pages.onboardingPortal.nextSteps')}</h3>
          <ol style={{ lineHeight: 1.8, color: 'var(--text-2)', marginLeft: 20, fontSize: 13.5 }}>
            <li>{t('pages.onboardingPortal.waitForAnAdministratorToReview')}</li>
            <li>
              <a href={portalMode ? `/agent-portal/status/${requestId}` : `/onboarding/requests/${requestId}`} style={{ color: 'var(--accent)', fontWeight: 600 }}>
                {t('pages.onboardingPortal.checkStatusAndDownloadFiles')}
              </a>{' '}
              {t('pages.onboardingPortal.onceApproved')}
            </li>
            <li>{t('pages.onboardingPortal.followTheSetupGuideToConfigure')}</li>
            <li>{t('pages.onboardingPortal.startUsingMCPToolsInYour')}</li>
          </ol>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href={portalMode ? `/agent-portal/status/${requestId}` : `/onboarding/requests/${requestId}`} className="ent-btn primary">
            <Icon name="signal" />
            {t('pages.onboardingPortal.checkStatus')}
          </a>
          <button
            className="ent-btn"
            onClick={() => {
              setStep(1);
              setFormData({
                agentName: '',
                role: 'developer',
                serviceIds: [],
                description: ''
              });
              setRequestId(null);
              setAgentId(null);
              setError(null);
            }}
          >
            <Icon name="plus" />
            {t('pages.onboardingPortal.createAnotherRequest')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-content">
      <h1>
        <Icon name="rocket" size={20} /> {t('pages.onboardingPortal.agentOnboarding')}
      </h1>
      <p style={{ marginBottom: 24 }}>{t('pages.onboardingPortal.requestAccessToMCPServicesIn')}</p>

      {/* Error Message */}
      {error && (
        <div className="notice error">
          <Icon name="warning" />
          {error}
        </div>
      )}

      {/* Step Indicator */}
      {step !== 5 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, gap: 8 }}>
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  background: step >= s ? 'var(--accent)' : step > s ? 'var(--success)' : 'var(--surface-2)',
                  color: step >= s ? '#fff' : 'var(--text-3)',
                  border: `2px solid ${step >= s ? 'var(--accent)' : step > s ? 'var(--success)' : 'var(--border)'}`,
                  transition: 'all 0.2s var(--ease)',
                  flexShrink: 0
                }}
              >
                {step > s ? <Icon name="check" size={14} /> : s}
              </div>
              {s < 4 && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    background: step > s ? 'var(--success)' : 'var(--border)',
                    transition: 'all 0.2s var(--ease)',
                    minWidth: 12
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Form Sections */}
      <form onSubmit={handleSubmit}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}

        {/* Navigation Buttons */}
        {step !== 5 && (
          <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 24 }}>
            <button type="button" className="ent-btn" onClick={handlePrevious} disabled={step === 1}>
              <Icon name="chevronRight" style={{ transform: 'rotate(180deg)' }} />
              {t('pages.onboardingPortal.previous')}
            </button>

            {step < 4 ? (
              <button type="button" className="ent-btn primary" onClick={handleNext}>
                {t('pages.onboardingPortal.next')}
                <Icon name="chevronRight" />
              </button>
            ) : (
              <button type="submit" className="ent-btn primary" disabled={loading}>
                <Icon name="checkCircle" className={loading ? 'spin' : ''} />
                {loading ? t('pages.onboardingPortal.submitting') : t('pages.onboardingPortal.submitRequest')}
              </button>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default OnboardingPortal;
